/**
 * 計助: 96穴プレート（CPE +/−）から複数サンプルの TCID50 / LD50 / EC50 を一括算出するエンジン層。
 *
 * 実運用フロー（添付 docs/TCID50.ods 形式）に合わせる:
 *   - 96穴プレート = 8 希釈段（10^-1〜10^-8）× 12 ウェル列
 *   - 1 サンプル = 連続する数列（4 連 / 12 連 など）。割当は「sample名」ヘッダ行で行う。
 *   - 各ウェルは + / − （CPE 陽性 / 陰性）。空欄は「そのウェルは使っていない」とみなし総数から除外。
 *
 * このモジュールは UI / i18n に非依存の純粋関数群。警告は機械可読なコード（{ code, ... }）で返し、
 * 実際の文言は呼び出し側（UI）でローカライズする。統計計算は検証済みの
 * `calculateTCID50` / `calculateDoseResponse` をそのまま呼ぶ薄いオーケストレーション層。
 */

import {
  calculateTCID50,
  calculateDoseResponse,
  type TCID50Method,
  type Method,
} from './statistics/doseResponse';
import type { DilutionData } from './statistics/tcid50';
import { KeisukeAdapterError } from './keisukeAdapterI18n';

/** ウェルの判定 */
export type WellSign = 'positive' | 'negative' | 'empty';

/** プレート解析の対象（用量反応系） */
export type PlateAnalysisType = 'tcid50' | 'ld50' | 'ec50' | 'ec90';

/** 構造化された警告（UI で i18n する） */
export interface PlateWarning {
  code: string;
  params?: Record<string, string | number>;
}

/** 1 サンプル・1 希釈段の集計結果 */
export interface PlateDilutionRow {
  /** 表示用ラベル（例: "10^-3"） */
  label: string;
  /** 数値レベル。TCID50 は希釈倍率（10^-3 → 1000）、LD50/EC50 は用量そのもの */
  level: number;
  /** CPE 陽性ウェル数 */
  positive: number;
  /** 使用ウェル数（陽性 + 陰性。空欄は含めない） */
  total: number;
  /** 感染率 positive/total（total=0 のとき NaN） */
  rate: number;
}

/** 1 サンプル分の集計 */
export interface PlateSample {
  name: string;
  /** 希釈段（入力順を保持） */
  dilutions: PlateDilutionRow[];
}

export interface PlateParseResult {
  samples: PlateSample[];
  warnings: PlateWarning[];
}

/** 解析対象セルの判定（全角・1/0・各種マーカに寛容） */
export function classifyWell(raw: unknown): WellSign {
  if (raw === null || raw === undefined) return 'empty';
  const s = String(raw).trim();
  if (s === '') return 'empty';
  const lower = s.toLowerCase();
  if (['+', '＋', '1', 'p', 'pos', '●', '○', '◯', '✓', '✔', 'y', 'yes', 't', 'true'].includes(lower)) {
    return 'positive';
  }
  if (['-', '−', '－', '–', '—', '0', 'n', 'neg', '×', 'x', '✕', 'no', 'f', 'false'].includes(lower)) {
    return 'negative';
  }
  // 想定外トークンは「未使用ウェル」として総数に含めない（誤集計を避ける）
  return 'empty';
}

/**
 * 希釈段ラベルを数値レベルに変換。
 * - TCID50 系の "10^-k" / "10^k" / "10E-k" は希釈倍率 10^|k| として返す（reciprocal dilution factor）。
 * - "1e-3" や素の数値はその値をそのまま返す（LD50/EC50 の用量はこちら）。
 * 解釈できなければ null。
 */
export function parseDilutionLevel(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  // 10^-3 / 10^3 / 10 ^ -3 / 10E-3 / 10e3 （上付き運用の素朴表現）
  const powMatch = s.match(/^10\s*[\^eE]\s*([+-−–]?\s*\d+)\s*$/);
  if (powMatch) {
    const expRaw = powMatch[1]!.replace(/[−–]/g, '-').replace(/\s+/g, '');
    const exp = Number(expRaw);
    if (Number.isFinite(exp)) return Math.pow(10, Math.abs(exp));
  }
  // 通常の数値（1e-3, 0.001, 1000 など）
  const n = Number(s.replace(/[−–]/g, '-'));
  return Number.isFinite(n) ? n : null;
}

/** クリップボード/貼り付けテキストを 2 次元配列へ（タブ優先、無ければカンマ）。空セルは保持。 */
export function parsePlateText(text: string): string[][] {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n');
  // 末尾の空行のみ落とす（途中の空行はブロック区切りとして残す）
  while (lines.length > 0 && lines[lines.length - 1]!.trim() === '') lines.pop();
  const hasTab = normalized.includes('\t');
  return lines.map((line) => (hasTab ? line.split('\t') : line.split(',')).map((c) => c.trim()));
}

function isRowEmpty(row: string[]): boolean {
  return row.every((c) => c.trim() === '');
}

/** 全空行でブロック（プレート）に分割。複数プレートを縦に貼った場合に対応。 */
export function splitPlateBlocks(matrix: string[][]): string[][][] {
  const blocks: string[][][] = [];
  let current: string[][] = [];
  for (const row of matrix) {
    if (isRowEmpty(row)) {
      if (current.length > 0) {
        blocks.push(current);
        current = [];
      }
    } else {
      current.push(row);
    }
  }
  if (current.length > 0) blocks.push(current);
  return blocks;
}

/** 全行で空の先頭列を落とし、ラベル列を 0 に揃える */
function trimLeadingEmptyColumns(block: string[][]): string[][] {
  if (block.length === 0) return block;
  const maxLen = Math.max(...block.map((r) => r.length));
  let firstNonEmptyCol = 0;
  for (let c = 0; c < maxLen; c += 1) {
    const colEmpty = block.every((r) => (r[c] ?? '').trim() === '');
    if (!colEmpty) {
      firstNonEmptyCol = c;
      break;
    }
  }
  if (firstNonEmptyCol === 0) return block;
  return block.map((r) => r.slice(firstNonEmptyCol));
}

interface BlockParse {
  /** sampleName ごとの dilutionLabel → 集計 */
  samples: Map<string, Map<string, PlateDilutionRow>>;
  /** サンプル登場順 */
  order: string[];
  warnings: PlateWarning[];
}

/** 行が「データ行（希釈段 + 本体の +/−）」か判定 */
function isDataRow(row: string[]): boolean {
  if (row.length < 2) return false;
  if (parseDilutionLevel(row[0]) === null) return false;
  const body = row.slice(1);
  return body.some((c) => classifyWell(c) !== 'empty');
}

/** 1 ブロック（= 1 プレート相当）を集計 */
function parseBlock(rawBlock: string[][]): BlockParse {
  const warnings: PlateWarning[] = [];
  const block = trimLeadingEmptyColumns(rawBlock);
  const samples = new Map<string, Map<string, PlateDilutionRow>>();
  const order: string[] = [];

  const dataRowIdx = block.findIndex(isDataRow);
  if (dataRowIdx === -1) {
    warnings.push({ code: 'plate.noDataRows' });
    return { samples, order, warnings };
  }

  // sample名 行の検出:
  //  1) 先頭セルが sample/サンプル/名/name 等のヘッダ語にマッチする行を最優先
  //     （名前が "X" や数値様でもヘッダで確実に拾える）
  //  2) 無ければデータ行直上から遡り、本体が「全て数値（=ウェル番号行）」でなく
  //     非空セルを持つ行を採用
  const headerRe = /sample|サンプル|name|名/i;
  let sampleRow: string[] | null = null;
  for (let r = 0; r < dataRowIdx; r += 1) {
    if (headerRe.test((block[r]![0] ?? '').trim())) {
      sampleRow = block[r]!;
      break;
    }
  }
  if (!sampleRow) {
    for (let r = dataRowIdx - 1; r >= 0; r -= 1) {
      const body = block[r]!.slice(1);
      const nonEmpty = body.filter((c) => c.trim() !== '');
      if (nonEmpty.length === 0) continue;
      const allNumeric = nonEmpty.every((c) => parseDilutionLevel(c) !== null);
      if (!allNumeric) {
        sampleRow = block[r]!;
        break;
      }
    }
  }

  // 本体の最大列数
  const bodyWidth = Math.max(...block.slice(dataRowIdx).map((r) => r.length - 1), 0);

  // 列 → サンプル名（sample名 行を前方補完: Excel の結合セル貼付で空になる場合に対応）
  const colSampleName: string[] = [];
  if (sampleRow) {
    let last = '';
    for (let c = 0; c < bodyWidth; c += 1) {
      const cell = (sampleRow[c + 1] ?? '').trim();
      if (cell !== '') last = cell;
      colSampleName[c] = last;
    }
  } else {
    warnings.push({ code: 'plate.noSampleRow' });
    // フォールバック: 各本体列を独立サンプル（列番号）として扱う
    for (let c = 0; c < bodyWidth; c += 1) colSampleName[c] = `${c + 1}`;
  }

  const ensure = (name: string): Map<string, PlateDilutionRow> => {
    let m = samples.get(name);
    if (!m) {
      m = new Map();
      samples.set(name, m);
      order.push(name);
    }
    return m;
  };

  for (let r = dataRowIdx; r < block.length; r += 1) {
    const row = block[r]!;
    if (!isDataRow(row)) continue;
    const label = row[0]!.trim();
    const level = parseDilutionLevel(label);
    if (level === null) continue;
    for (let c = 0; c < bodyWidth; c += 1) {
      const name = colSampleName[c];
      if (!name) continue;
      const sign = classifyWell(row[c + 1]);
      if (sign === 'empty') continue;
      const m = ensure(name);
      let agg = m.get(label);
      if (!agg) {
        agg = { label, level, positive: 0, total: 0, rate: NaN };
        m.set(label, agg);
      }
      agg.total += 1;
      if (sign === 'positive') agg.positive += 1;
    }
  }

  return { samples, order, warnings };
}

/**
 * 貼り付けテキスト全体（複数プレート可）をサンプルごとの集計へ。
 * 同名サンプルが複数ブロックに現れた場合は (サンプル名, 希釈ラベル) 単位で合算する。
 */
export function parsePlate(text: string): PlateParseResult {
  const matrix = parsePlateText(text);
  const blocks = splitPlateBlocks(matrix);
  const warnings: PlateWarning[] = [];

  // サンプル名 → (ラベル → 集計)、登場順
  const merged = new Map<string, Map<string, PlateDilutionRow>>();
  const order: string[] = [];

  for (const block of blocks) {
    const parsed = parseBlock(block);
    warnings.push(...parsed.warnings);
    for (const name of parsed.order) {
      const src = parsed.samples.get(name)!;
      let dst = merged.get(name);
      if (!dst) {
        dst = new Map();
        merged.set(name, dst);
        order.push(name);
      }
      for (const [label, row] of src) {
        const existing = dst.get(label);
        if (existing) {
          existing.positive += row.positive;
          existing.total += row.total;
        } else {
          dst.set(label, { ...row });
        }
      }
    }
  }

  const samples: PlateSample[] = order.map((name) => {
    const dilutions = Array.from(merged.get(name)!.values()).map((d) => ({
      ...d,
      rate: d.total > 0 ? d.positive / d.total : NaN,
    }));
    // 希釈段は level 昇順（薄い→濃いの提示が安定する）
    dilutions.sort((a, b) => a.level - b.level);
    return { name, dilutions };
  });

  if (samples.length === 0) warnings.push({ code: 'plate.noSamples' });

  return { samples, warnings };
}

/** バッチ結果 1 行 */
export interface PlateBatchRow {
  sample: string;
  /** 推定値（TCID50 / LD50 / EC50 / EC90） */
  value: number | null;
  /** log10 値（TCID50 のみ。他は null） */
  valueLog10: number | null;
  ciLower: number | null;
  ciUpper: number | null;
  /** 使用した希釈段数 */
  levels: number;
  /** 1 段あたりの最大ウェル数（連数の目安） */
  replicates: number;
  method: string;
  warnings: PlateWarning[];
}

export interface PlateBatchOptions {
  analysis: PlateAnalysisType;
  /** TCID50: 'spearman-karber' | 'reed-muench' / LD50・EC50: 'probit' | 'logistic' */
  method: Method;
}

/** 感染率が希釈の進行（level 昇順）に対して単調非増加かを確認 */
function checkMonotonic(dilutions: PlateDilutionRow[]): PlateWarning | null {
  const usable = dilutions.filter((d) => d.total > 0);
  for (let i = 1; i < usable.length; i += 1) {
    if (usable[i]!.rate > usable[i - 1]!.rate + 1e-9) {
      return { code: 'plate.nonMonotonic' };
    }
  }
  return null;
}

function mapAdapterError(err: unknown): PlateWarning {
  if (err instanceof KeisukeAdapterError) {
    return { code: `plate.calc.${err.code}` };
  }
  return { code: 'plate.calc.unknown' };
}

/** 1 サンプルを解析（失敗時は value=null + 警告） */
export function runPlateSample(sample: PlateSample, opts: PlateBatchOptions): PlateBatchRow {
  const usable = sample.dilutions.filter((d) => d.total > 0);
  const replicates = usable.reduce((m, d) => Math.max(m, d.total), 0);
  const base: PlateBatchRow = {
    sample: sample.name,
    value: null,
    valueLog10: null,
    ciLower: null,
    ciUpper: null,
    levels: usable.length,
    replicates,
    method: opts.method,
    warnings: [],
  };

  if (usable.length < 2) {
    return { ...base, warnings: [{ code: 'plate.calc.minTwoPoints' }] };
  }
  const mono = checkMonotonic(usable);
  const warnings: PlateWarning[] = mono ? [mono] : [];

  try {
    if (opts.analysis === 'tcid50') {
      const data: DilutionData[] = usable.map((d) => ({
        dilution: d.level,
        infected: d.positive,
        total: d.total,
        infectionRate: d.rate,
      }));
      const method: TCID50Method = opts.method === 'reed-muench' ? 'reed-muench' : 'spearman-karber';
      const res = calculateTCID50(data, method);
      return {
        ...base,
        value: res.value,
        valueLog10: res.valueLog10 ?? null,
        ciLower: res.confidenceInterval?.lower ?? null,
        ciUpper: res.confidenceInterval?.upper ?? null,
        method,
        warnings,
      };
    }

    // LD50 / EC50 / EC90: probit / logistic GLM（dead/total）
    const type = opts.analysis === 'ec50' ? 'ec50' : opts.analysis === 'ec90' ? 'ec90' : 'ld50';
    const method: Method = opts.method === 'logistic' ? 'logistic' : 'probit';
    const doses = usable.map((d) => d.level);
    const dead = usable.map((d) => d.positive);
    const total = usable.map((d) => d.total);
    const responses = usable.map((d) => d.rate);
    const res = calculateDoseResponse(type, doses, responses, method, dead, total);
    return {
      ...base,
      value: res.value,
      valueLog10: null,
      ciLower: res.confidenceInterval?.lower ?? null,
      ciUpper: res.confidenceInterval?.upper ?? null,
      method,
      warnings,
    };
  } catch (err) {
    return { ...base, warnings: [...warnings, mapAdapterError(err)] };
  }
}

/** 全サンプルをバッチ解析 */
export function runPlateBatch(
  samples: PlateSample[],
  opts: PlateBatchOptions
): PlateBatchRow[] {
  return samples.map((s) => runPlateSample(s, opts));
}

/** バッチ結果を CSV 文字列へ（Excel 貼り戻し用） */
export function plateBatchToCsv(rows: PlateBatchRow[], analysis: PlateAnalysisType): string {
  const valueHeader = analysis === 'tcid50' ? 'TCID50' : analysis.toUpperCase();
  const header = [
    'sample',
    valueHeader,
    ...(analysis === 'tcid50' ? ['log10'] : []),
    'Approx. interval lower',
    'Approx. interval upper',
    'levels',
    'replicates',
    'method',
    'warnings',
  ];
  const fmt = (n: number | null): string =>
    n === null || !Number.isFinite(n) ? '' : String(n);
  const esc = (s: string): string => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const lines = [header.join(',')];
  for (const r of rows) {
    const cells = [
      r.sample,
      fmt(r.value),
      ...(analysis === 'tcid50' ? [fmt(r.valueLog10)] : []),
      fmt(r.ciLower),
      fmt(r.ciUpper),
      String(r.levels),
      String(r.replicates),
      r.method,
      r.warnings.map((w) => w.code).join('; '),
    ];
    lines.push(cells.map((c) => esc(c)).join(','));
  }
  return lines.join('\n');
}

/** プレート1サンプルをワークスペース表へ載せるときの行データ */
export type PlateWorkspaceRow = Record<string, string | number>;

export interface PlateWorkspacePayload {
  sampleName: string;
  headers: [string, string, string];
  rows: PlateWorkspaceRow[];
  drType: PlateAnalysisType;
  drMethod: Method;
}

/** 1 サンプルの希釈集計を 3 列表（用量反応コンパクト表）へ変換 */
export function buildPlateWorkspacePayload(
  sample: PlateSample,
  analysis: PlateAnalysisType,
  method: Method
): PlateWorkspacePayload {
  const usable = sample.dilutions.filter((d) => d.total > 0);
  if (analysis === 'tcid50') {
    const headers: [string, string, string] = ['Dilution', 'Infected', 'Total'];
    const rows = usable.map((d) => ({
      [headers[0]]: d.level,
      [headers[1]]: d.positive,
      [headers[2]]: d.total,
    }));
    return { sampleName: sample.name, headers, rows, drType: analysis, drMethod: method };
  }
  const headers: [string, string, string] = ['Dose', 'Effect', 'Total'];
  const rows = usable.map((d) => ({
    [headers[0]]: d.level,
    [headers[1]]: d.positive,
    [headers[2]]: d.total,
  }));
  return { sampleName: sample.name, headers, rows, drType: analysis, drMethod: method };
}
